"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MessageSquare, ChevronUp, CreditCard, LogOut, ExternalLink, Search, Plus } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { ProductSwitcher } from "@/components/product-switcher";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrgShares } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrgSearchDialog } from "./org-search-dialog";
import { OrganizationSwitcher } from "./organization-switcher";
import { StudioNav } from "./studio-nav";
import type { Organization } from "@/lib/types";
interface OrgSidebarProps {
  organizationId: string;
  currentShareId?: string;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (org: Organization | null) => void;
  className?: string;
}
interface OrgShareItem {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}
type OrgShareHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "share";
  key: string;
  share: OrgShareItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 32;
const ROW_HEIGHT_SPACER = 16;
const ROW_HEIGHT_SHARE = 60;
interface OrgShareHistoryRowProps {
  rows: OrgShareHistoryRow[];
  currentShareId?: string;
  onShareSelect: (shareId: string) => void;
}
function getOrgShareRowHeight(index: number, {
  rows
}: OrgShareHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_SHARE;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupSharesByDate(shares: OrgShareItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as OrgShareItem[],
    yesterday: [] as OrgShareItem[],
    lastWeek: [] as OrgShareItem[],
    older: [] as OrgShareItem[]
  };
  shares.forEach(share => {
    const shareDate = new Date(share.updatedAt);
    if (shareDate.toDateString() === today.toDateString()) {
      groups.today.push(share);
    } else if (shareDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(share);
    } else if (shareDate >= lastWeek) {
      groups.lastWeek.push(share);
    } else {
      groups.older.push(share);
    }
  });
  return groups;
}
function OrgShareRowComponent(t0) {
  const $ = _c(29);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentShareId,
    onShareSelect
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    share
  } = row;
  const t1 = currentShareId === share.id;
  let t2;
  if ($[8] !== onShareSelect || $[9] !== share.id) {
    t2 = () => onShareSelect(share.id);
    $[8] = onShareSelect;
    $[9] = share.id;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== share.title) {
    t4 = <div className="truncate text-sm font-medium mb-0.5">{share.title}</div>;
    $[12] = share.title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== share.updatedAt) {
    t5 = formatDate(share.updatedAt);
    $[14] = share.updatedAt;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <div className="text-xs text-muted-foreground">{t5}</div>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div className="flex-1 min-w-0">{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t7) {
    t8 = <div className="relative h-full px-2 pb-1"><div className="relative h-full"><SidebarMenuButton isActive={t1} onClick={t2} className="h-full! w-full justify-start gap-3 group relative" type="button">{t3}{t7}</SidebarMenuButton></div></div>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== ariaAttributes || $[26] !== style || $[27] !== t8) {
    t9 = <div {...ariaAttributes} style={style}>{t8}</div>;
    $[25] = ariaAttributes;
    $[26] = style;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
export function OrgSidebar(t0) {
  const $ = _c(107);
  const {
    organizationId,
    currentShareId,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className
  } = t0;
  const listContainerRef = useRef(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    data: orgSharesData,
    isLoading: isSharesLoading
  } = useOrgShares(organizationId);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  orgSharesData?.shares;
  let t1;
  if ($[0] !== orgSharesData?.shares) {
    t1 = orgSharesData?.shares ?? [];
    $[0] = orgSharesData?.shares;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shares = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(window.navigator.platform));
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== isMac) {
    t4 = () => {
      const handleKeyDown = event => {
        const key = event.key.toLowerCase();
        const isSearchShortcut = isMac ? event.metaKey && key === "k" && !event.altKey && !event.ctrlKey : event.altKey && key === "k" && !event.metaKey && !event.ctrlKey;
        if (!isSearchShortcut || event.shiftKey || event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        setIsSearchOpen(true);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [isMac];
    $[4] = isMac;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== posthog || $[8] !== queryClient || $[9] !== router || $[10] !== signOut) {
    t6 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[7] = posthog;
    $[8] = queryClient;
    $[9] = router;
    $[10] = signOut;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const logout = t6;
  let t7;
  if ($[12] !== organizationId || $[13] !== router) {
    t7 = shareId => {
      router.push(`/org/${organizationId}/chat/${shareId}`);
    };
    $[12] = organizationId;
    $[13] = router;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleShareSelect = t7;
  let t8;
  if ($[15] !== shares) {
    t8 = groupSharesByDate([...shares].sort(_temp));
    $[15] = shares;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const shareGroups = t8;
  let rows;
  if ($[17] !== shareGroups.lastWeek || $[18] !== shareGroups.older || $[19] !== shareGroups.today || $[20] !== shareGroups.yesterday) {
    const groups = [{
      title: "Today",
      shares: shareGroups.today
    }, {
      title: "Yesterday",
      shares: shareGroups.yesterday
    }, {
      title: "Last 7 days",
      shares: shareGroups.lastWeek
    }, {
      title: "Older",
      shares: shareGroups.older
    }];
    rows = [];
    groups.forEach((t9, groupIndex) => {
      const {
        title,
        shares: groupedShares
      } = t9;
      if (groupedShares.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedShares.forEach(share => {
        rows.push({
          type: "share",
          key: `share-${share.id}`,
          share
        });
      });
      const hasNextGroupWithShares = groups.slice(groupIndex + 1).some(_temp2);
      if (hasNextGroupWithShares) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[17] = shareGroups.lastWeek;
    $[18] = shareGroups.older;
    $[19] = shareGroups.today;
    $[20] = shareGroups.yesterday;
    $[21] = rows;
  } else {
    rows = $[21];
  }
  const historyRows = rows;
  let t9;
  if ($[22] !== currentShareId || $[23] !== handleShareSelect || $[24] !== historyRows) {
    t9 = {
      rows: historyRows,
      currentShareId,
      onShareSelect: handleShareSelect
    };
    $[22] = currentShareId;
    $[23] = handleShareSelect;
    $[24] = historyRows;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const rowProps = t9;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t10;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ChatSidebarSkeleton organization={null} />;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  if (!user) {
    let t10;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ProductSwitcher />;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    let t12;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="font-medium mb-2">Sign in required</div>;
      t12 = <p className="text-muted-foreground mb-3">Please sign in to view organizations and shared chats.</p>;
      $[28] = t11;
      $[29] = t12;
    } else {
      t11 = $[28];
      t12 = $[29];
    }
    let t13;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t10}<div className="w-full rounded-md border p-4 text-sm">{t11}{t12}<div className="flex items-center justify-end gap-2">{t13}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== className) {
      t15 = <Sidebar className={className}>{t14}</Sidebar>;
      $[32] = className;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    return t15;
  }
  if (isSharesLoading || isOrgLoading) {
    let t10;
    if ($[34] !== selectedOrganization) {
      t10 = <ChatSidebarSkeleton organization={selectedOrganization} />;
      $[34] = selectedOrganization;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    return t10;
  }
  let t10;
  if ($[36] !== className) {
    t10 = cn(className, "max-md:hidden");
    $[36] = className;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarMenuItem><ProductSwitcher /></SidebarMenuItem>;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarHeader><SidebarMenu>{t11}<SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="New Chat" className="border border-border"><Link href="/"><Plus className="h-4 w-4" /><span>New Chat</span></Link></SidebarMenuButton></SidebarMenuItem></SidebarMenu><StudioNav /></SidebarHeader>;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== onSelectOrganization || $[42] !== organizations || $[43] !== selectedOrganization) {
    t14 = organizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[41] = onSelectOrganization;
    $[42] = organizations;
    $[43] = selectedOrganization;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  let t16;
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsSearchOpen(true);
    t16 = <Search className="h-4 w-4" />;
    t17 = <span>Search Chats</span>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t15 = $[45];
    t16 = $[46];
    t17 = $[47];
  }
  const t18 = isMac ? "\u2318K" : "Alt+K";
  let t19;
  if ($[48] !== t18) {
    t19 = <SidebarMenu className="px-2"><SidebarMenuItem><SidebarMenuButton type="button" tooltip="Search Chats" onClick={t15}>{t16}{t17}<kbd className="ml-auto text-xs font-medium text-muted-foreground opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 group-data-[collapsible=icon]:hidden">{t18}</kbd></SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t14 || $[51] !== t19) {
    t20 = <div>{t13}{t14}{t19}</div>;
    $[50] = t14;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== historyRows || $[54] !== rowProps || $[55] !== shares.length) {
    t21 = shares.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No shared chats</p><p className="text-xs text-muted-foreground">Share a chat with this organization to see it here</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={OrgShareRowComponent} rowCount={historyRows.length} rowHeight={getOrgShareRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[53] = historyRows;
    $[54] = rowProps;
    $[55] = shares.length;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== isHistoryHidden || $[58] !== t21) {
    t22 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t21}</div>;
    $[57] = isHistoryHidden;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t20 || $[61] !== t22) {
    t23 = <SidebarContent className="overflow-hidden pb-2">{t20}{t22}</SidebarContent>;
    $[60] = t20;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== isOrgLoading || $[64] !== selectedOrganization) {
    t24 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={isOrgLoading} /></div>;
    $[63] = isOrgLoading;
    $[64] = selectedOrganization;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  const t25 = user?.name ?? "User";
  let t26;
  if ($[66] !== user?.name) {
    t26 = user?.name?.split(" ").map(_temp3).join("").toUpperCase().slice(0, 2) ?? "U";
    $[66] = user?.name;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t26) {
    t27 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t26}</span></div>;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  const t28 = user?.name;
  let t29;
  if ($[70] !== t28) {
    t29 = <span className="truncate font-semibold">{t28}</span>;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  const t30 = user?.email;
  let t31;
  if ($[72] !== t30) {
    t31 = <span className="truncate text-xs text-muted-foreground">{t30}</span>;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t29 || $[75] !== t31) {
    t32 = <div className="grid flex-1 text-left text-sm leading-tight">{t29}{t31}</div>;
    $[74] = t29;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t25 || $[79] !== t27 || $[80] !== t32) {
    t34 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t25} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t27}{t32}{t33}</SidebarMenuButton></DropdownMenuTrigger>;
    $[78] = t25;
    $[79] = t27;
    $[80] = t32;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  let t36;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t36 = <DropdownMenuSeparator />;
    $[82] = t35;
    $[83] = t36;
  } else {
    t35 = $[82];
    t36 = $[83];
  }
  let t37;
  let t38;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t38 = <DropdownMenuSeparator />;
    $[84] = t37;
    $[85] = t38;
  } else {
    t37 = $[84];
    t38 = $[85];
  }
  let t39;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span>Theme</span>;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  let t41;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp4}>{t39}<div onClick={_temp5} onKeyDown={_temp6}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t41 = <DropdownMenuSeparator />;
    $[87] = t40;
    $[88] = t41;
  } else {
    t40 = $[87];
    t41 = $[88];
  }
  let t42;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <LogOut className="mr-2 h-4 w-4" />;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== logout) {
    t43 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t35}{t36}{t37}{t38}{t40}{t41}<DropdownMenuItem onClick={logout}>{t42}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[90] = logout;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== t34 || $[93] !== t43) {
    t44 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t34}{t43}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[92] = t34;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== t24 || $[96] !== t44) {
    t45 = <SidebarFooter>{t24}{t44}</SidebarFooter>;
    $[95] = t24;
    $[96] = t44;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== handleShareSelect || $[99] !== isSearchOpen || $[100] !== organizationId) {
    t46 = <OrgSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} organizationId={organizationId} onShareSelect={handleShareSelect} />;
    $[98] = handleShareSelect;
    $[99] = isSearchOpen;
    $[100] = organizationId;
    $[101] = t46;
  } else {
    t46 = $[101];
  }
  let t47;
  if ($[102] !== t10 || $[103] !== t23 || $[104] !== t45 || $[105] !== t46) {
    t47 = <Sidebar collapsible="icon" className={t10}>{t12}{t23}{t45}{t46}</Sidebar>;
    $[102] = t10;
    $[103] = t23;
    $[104] = t45;
    $[105] = t46;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  return t47;
}
function _temp6(event_2) {
  return event_2.stopPropagation();
}
function _temp5(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_0) {
  return event_0.preventDefault();
}
function _temp3(n) {
  return n[0];
}
function _temp2(group) {
  return group.shares.length > 0;
}
function _temp(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}